/**
 * web/src/shell/bridge.ts — PARENT-OWNED (W0). The only way feature UI
 * modules talk to the app: API base, session, auth headers.
 *
 * Feature modules (web/src/requests/ui.ts, web/src/questions/ui.ts, …) import
 * from here and export a `mount(container: HTMLElement)` function; the parent
 * wires nav + mount calls in main.ts / index.html during integration (W3).
 * Children must NOT edit this file or main.ts.
 */

/** Empty in dev (vite proxies /api + /ws), set at build time for Pages. */
export const API_BASE: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE) || '';

const LS_TOKEN = 'pear_token';
const LS_COUPLE_ID = 'pear_couple_id';
const LS_ROLE = 'pear_role';
const LS_MEMBER_ID = 'pear_member_id';

export function getToken(): string | null {
  return localStorage.getItem(LS_TOKEN);
}

export interface Session {
  token: string;
  coupleId: string;
  role: 'A' | 'B';
  memberId: string;
}

export function session(): Session | null {
  const token = localStorage.getItem(LS_TOKEN);
  const coupleId = localStorage.getItem(LS_COUPLE_ID);
  const role = localStorage.getItem(LS_ROLE);
  const memberId = localStorage.getItem(LS_MEMBER_ID);
  if (!token || !coupleId || !role || !memberId) return null;
  return { token, coupleId, role: role as 'A' | 'B', memberId };
}

/** JSON fetch against the worker. Throws Error(JSON.stringify(body)) on non-2xx. */
export async function api<T = unknown>(
  path: string,
  opts?: { method?: 'GET' | 'POST' | 'PUT'; body?: Record<string, unknown> },
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = getToken();
  if (token) headers['authorization'] = `Bearer ${token}`;
  const res = await fetch(API_BASE + path, {
    method: opts?.body ? (opts.method ?? 'POST') : (opts?.method ?? 'GET'),
    headers,
    body: opts?.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(JSON.stringify(data ?? { status: res.status }));
  return data as T;
}

/** Escape for innerHTML interpolation (feature UIs build markup as strings). */
export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}
